/**
 * User settings — hybrid (localStorage + Supabase).
 *
 * Anonymous users: localStorage only.
 * Signed-in users: Supabase is server-truth, localStorage is a paint cache.
 *
 * The hybrid orchestration happens in `hooks/useSettings.ts`; this module
 * exposes only the raw read/write primitives.
 */

import { supabase } from '@/lib/supabase/client';
import type {
  TranslationLanguage,
  TafsirLanguage,
  Theme,
} from '@/types/domain';

export type UserSettings = {
  theme: Theme;
  font_size: number;
  translation_language: TranslationLanguage;
  show_transliteration: boolean;
  show_tafsir: boolean;
  tafsir_language: TafsirLanguage;
  reciter_id: number | null;
  autoplay_next_ayah: boolean;
};

export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  font_size: 3,
  translation_language: 'en',
  show_transliteration: false,
  show_tafsir: false,
  tafsir_language: 'en',
  reciter_id: null,
  autoplay_next_ayah: false,
};

const STORAGE_KEY = 'alhudah:settings';

export function loadLocalSettings(): UserSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<UserSettings>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveLocalSettings(settings: UserSettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* quota or private mode — ignore */
  }
}

export function clearLocalSettings(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export async function fetchRemoteSettings(
  userId: string,
): Promise<UserSettings | null> {
  const { data, error } = await supabase
    .from('user_settings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  // The user_settings table stores theme / translation_language /
  // tafsir_language as plain text (no Postgres enum), so the generated
  // Supabase types widen them to `string`. The app's union types are
  // narrower (e.g. 'light' | 'dark' | 'system'). We trust values
  // written by the app and cast at the boundary. If a row is corrupted
  // to an out-of-union value, the UI's switch statements fall through
  // to defaults — no runtime crash.
  return {
    theme: data.theme as Theme,
    font_size: data.font_size,
    translation_language: data.translation_language as TranslationLanguage,
    show_transliteration: data.show_transliteration,
    show_tafsir: data.show_tafsir,
    tafsir_language: data.tafsir_language as TafsirLanguage,
    reciter_id: data.reciter_id,
    autoplay_next_ayah: data.autoplay_next_ayah,
  };
}

export async function upsertRemoteSettings(
  userId: string,
  settings: UserSettings,
): Promise<void> {
  const { error } = await supabase
    .from('user_settings')
    .upsert(
      { user_id: userId, ...settings },
      { onConflict: 'user_id' },
    );
  if (error) throw error;
}
